import { useEffect, useRef, useState, useCallback } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Home, History, Shield, UserCircle2, Flag, ChevronLeft, ChevronRight, User, Settings, LogOut, BookOpen, CalendarClock, Bell } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import apiClient from "../api/client";
import { logWarn } from "../lib/logger";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: Home, testId: "nav-dashboard-link" },
  { to: "/game/new", label: "Start Game", icon: Flag, testId: "nav-start-game-link" },
  { to: "/tee-times", label: "Tee Times", icon: CalendarClock, testId: "nav-tee-times-link" },
  { to: "/history", label: "Rounds", icon: History, testId: "nav-history-link" },
  { to: "/rules", label: "Rules", icon: BookOpen, testId: "nav-rules-link" },
  { to: "/profile", label: "Profile", icon: UserCircle2, testId: "nav-profile-link" },
];

export default function AppLayout({ children }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const mobileNavScrollRef = useRef(null);
  const profileMenuRef = useRef(null);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const notifRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await apiClient.get("/notifications");
      setUnreadCount(res.data.unread_count || 0);
      setNotifications(res.data.notifications || []);
    } catch (e) { logWarn("Notification fetch:", e?.message); }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAllRead = async () => {
    try {
      await apiClient.put("/notifications/read-all");
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (e) { logWarn("Mark read:", e?.message); }
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const scrollMobileTabs = (direction) => {
    if (!mobileNavScrollRef.current) {
      return;
    }
    mobileNavScrollRef.current.scrollBy({
      left: direction === "left" ? -220 : 220,
      behavior: "smooth",
    });
  };

  const profileInitial = (user?.display_name || user?.name || "U").slice(0, 1).toUpperCase();

  return (
    <div className="min-h-screen bg-[var(--ggr-white)] text-[#2c2c2c] relative" data-testid="main-app-shell">
      {/* Lazy-loaded background image */}
      <img
        src="https://customer-assets.emergentagent.com/job_45bd2de5-1b36-4ca9-8b3c-1d2fe3211e61/artifacts/zejpeff3_App%20Login%20cover.jpg"
        alt=""
        aria-hidden="true"
        className="fixed inset-0 w-full h-full object-cover object-center pointer-events-none opacity-[0.34]"
        loading="lazy"
        decoding="async"
        style={{ zIndex: 0 }}
      />
      <div className="relative" style={{ zIndex: 1 }}>
      <header
        className="sticky top-0 z-30 border-b border-[#514a3f]/20 backdrop-blur-xl bg-gradient-to-r from-[#121212]/96 via-[#1a1a1a]/95 to-[#121212]/96 shadow-[0_10px_22px_rgba(0,0,0,0.28)] header-fade-in"
        data-testid="main-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-start justify-between gap-3">
          <Link to="/dashboard" className="flex items-start gap-3 min-w-0 flex-1" data-testid="header-logo-link">
            <img
              src={branding.logo_url}
              alt="Goldfields Game Ranch logo"
              className="h-11 w-11 rounded-lg object-cover border border-white/20"
              data-testid="header-logo-image"
            />
            <div className="min-w-0 pr-1">
              <h1 className="text-[#dfa625] text-[17px] sm:text-xl font-semibold tracking-wide heading-font leading-tight" data-testid="header-app-title">
                {branding.club_name}
              </h1>
              <p className="text-[#f9f9f7]/82 text-[10px] sm:text-xs uppercase tracking-[0.12em] leading-tight mt-0.5" data-testid="header-brand-subtitle">
                Goldfields Game Ranch
              </p>
              <p
                className="mt-1.5 inline-flex sm:hidden text-[#000000] text-[11px] font-medium bg-white/18 border border-white/25 backdrop-blur-sm rounded-full px-2.5 py-1"
                data-testid="header-user-name-mobile"
              >
                Welcome back, {user?.display_name || user?.name}
              </p>
            </div>
          </Link>

          <div className="flex items-start sm:items-center gap-2 sm:gap-3 shrink-0" data-testid="header-user-actions">
            <p
              className="hidden sm:inline-flex text-[#000000] text-xs sm:text-sm font-medium bg-white/16 border border-white/25 backdrop-blur-sm rounded-full px-3 py-1"
              data-testid="header-user-name"
            >
              Welcome back, <span className="font-semibold">{user?.display_name || user?.name}</span>
            </p>

            {user?.role === "admin" && (
              <button
                type="button"
                className="hidden md:inline-flex px-3 py-1.5 text-[11px] rounded-full border border-[#dfa625]/65 text-[#dfa625] hover:bg-[#dfa625] hover:text-[#101010] transition-all duration-200"
                onClick={() => navigate("/admin")}
                data-testid="header-admin-panel-button"
              >
                Admin Panel
              </button>
            )}

            {/* Notification Bell */}
            <div className="relative" ref={notifRef} data-testid="header-notifications-container">
              <button
                type="button"
                className="relative h-10 w-10 rounded-full border border-white/35 bg-white/18 backdrop-blur-sm text-white flex items-center justify-center transition-all duration-200 hover:scale-105 active:scale-95"
                onClick={() => { setShowNotifications((v) => !v); if (!showNotifications && unreadCount > 0) markAllRead(); }}
                data-testid="header-notifications-button"
              >
                <Bell size={18} />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 flex items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white px-1" data-testid="header-notifications-badge">
                    {unreadCount}
                  </span>
                )}
              </button>
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-72 max-h-80 overflow-y-auto rounded-xl border border-[#514a3f]/20 bg-white shadow-2xl p-2 z-50" data-testid="header-notifications-dropdown">
                  <p className="px-3 py-1.5 text-xs font-semibold text-[#514a3f]/60 uppercase tracking-wider">Notifications</p>
                  {notifications.length === 0 ? (
                    <p className="px-3 py-4 text-sm text-[#514a3f]/50 text-center">No notifications</p>
                  ) : (
                    notifications.slice(0, 10).map((n) => (
                      <div
                        key={n.id}
                        className={`px-3 py-2 rounded-lg text-sm ${n.read ? "text-[#514a3f]/60" : "text-[#514a3f] bg-[#dfa625]/6"}`}
                        data-testid={`notification-${n.id}`}
                      >
                        <p className="font-medium text-xs">{n.title}</p>
                        <p className="text-xs mt-0.5 leading-relaxed">{n.message}</p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            <div className="relative" ref={profileMenuRef} data-testid="header-profile-menu-container">
              <button
                type="button"
                className="h-10 w-10 rounded-full border border-white/35 bg-white/18 backdrop-blur-sm text-white text-sm font-semibold shadow-[0_8px_18px_rgba(0,0,0,0.25)] transition-all duration-200 hover:scale-105 active:scale-95"
                onClick={() => setIsProfileMenuOpen((prev) => !prev)}
                data-testid="header-profile-menu-button"
              >
                {user?.profile_picture_thumb_url || user?.profile_picture_url ? (
                  <img
                    src={user.profile_picture_thumb_url || user.profile_picture_url}
                    alt="User profile"
                    className="h-full w-full rounded-full object-cover"
                    data-testid="header-profile-menu-avatar-image"
                  />
                ) : (
                  <span data-testid="header-profile-menu-avatar-initial">{profileInitial}</span>
                )}
              </button>

              {isProfileMenuOpen && (
                <div
                  className="absolute right-0 mt-2 w-44 rounded-xl border border-[#514a3f]/20 bg-white shadow-[0_14px_24px_rgba(20,20,20,0.16)] p-2"
                  data-testid="header-profile-menu-dropdown"
                >
                  <button
                    type="button"
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[#514a3f] hover:bg-[#514a3f]/10 transition-colors"
                    onClick={() => {
                      setIsProfileMenuOpen(false);
                      navigate("/profile");
                    }}
                    data-testid="header-profile-menu-profile-item"
                  >
                    <User size={16} />
                    Profile
                  </button>
                  <button
                    type="button"
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[#514a3f] hover:bg-[#514a3f]/10 transition-colors"
                    onClick={() => {
                      setIsProfileMenuOpen(false);
                      navigate("/settings");
                    }}
                    data-testid="header-profile-menu-settings-item"
                  >
                    <Settings size={16} />
                    Settings
                  </button>
                  <button
                    type="button"
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-700 hover:bg-red-50 transition-colors"
                    onClick={() => {
                      setIsProfileMenuOpen(false);
                      logout();
                      navigate("/auth");
                    }}
                    data-testid="header-profile-menu-logout-item"
                  >
                    <LogOut size={16} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6 md:gap-8">
        <aside
          className="rounded-[24px] border border-[#514a3f]/20 bg-gradient-to-br from-white/90 to-[#f5f4f1]/85 backdrop-blur-xl shadow-[0_14px_36px_rgba(40,35,28,0.1)] h-fit relative"
          data-testid="app-sidebar"
        >
          <button
            type="button"
            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full border border-white/50 bg-white/45 backdrop-blur-md text-[#514a3f] flex items-center justify-center shadow-[0_8px_18px_rgba(40,35,28,0.16)] transition-all duration-200 hover:bg-white/70 hover:scale-105 active:scale-95"
            onClick={() => scrollMobileTabs("left")}
            data-testid="mobile-tab-scroll-left-button"
          >
            <ChevronLeft size={18} />
          </button>

          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full border border-white/50 bg-white/45 backdrop-blur-md text-[#514a3f] flex items-center justify-center shadow-[0_8px_18px_rgba(40,35,28,0.16)] transition-all duration-200 hover:bg-white/70 hover:scale-105 active:scale-95"
            onClick={() => scrollMobileTabs("right")}
            data-testid="mobile-tab-scroll-right-button"
          >
            <ChevronRight size={18} />
          </button>

          <nav
            ref={mobileNavScrollRef}
            className="p-4 flex lg:flex-col gap-3 overflow-x-auto scroll-smooth pr-14 pl-14 lg:pr-5 lg:pl-5"
            data-testid="app-sidebar-nav"
          >
            {navItems.map((item) => {
              const Icon = item.icon;
              const isStartGame = item.to === "/game/new";
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.testId}
                  className={({ isActive }) =>
                    [
                      "group min-w-[176px] lg:min-w-0 flex items-center gap-3 rounded-[18px] px-5 py-4 text-[15px] font-semibold tracking-[0.01em] transition-all duration-300 active:scale-[0.97]",
                      isActive
                        ? "bg-gradient-to-br from-[#514a3f] via-[#5f574a] to-[#3d372f] text-[#f9f9f7] shadow-[0_10px_24px_rgba(40,32,22,0.28)] border border-[#6a6152]"
                        : "text-[#4b473f] bg-white/70 border border-[#514a3f]/18 hover:bg-white hover:shadow-[0_8px_18px_rgba(40,35,28,0.12)]",
                    ].join(" ")
                  }
                >
                  <span className={`transition-transform duration-200 ${isStartGame ? "group-active:rotate-12 group-active:scale-125" : "group-active:scale-110"}`}>
                    <Icon size={21} strokeWidth={1.9} />
                  </span>
                  <span className="leading-none">{item.label}</span>
                </NavLink>
              );
            })}

            {user?.role === "admin" && (
              <NavLink
                to="/admin"
                data-testid="nav-admin-link"
                className={({ isActive }) =>
                  [
                    "group min-w-[176px] lg:min-w-0 flex items-center gap-3 rounded-[18px] px-5 py-4 text-[15px] font-semibold tracking-[0.01em] transition-all duration-300 active:scale-[0.97]",
                    isActive
                      ? "bg-gradient-to-br from-[#514a3f] via-[#5f574a] to-[#3d372f] text-[#f9f9f7] shadow-[0_10px_24px_rgba(40,32,22,0.28)] border border-[#6a6152]"
                      : "text-[#4b473f] bg-white/70 border border-[#514a3f]/18 hover:bg-white hover:shadow-[0_8px_18px_rgba(40,35,28,0.12)]",
                  ].join(" ")
                }
              >
                <span className="group-active:scale-110 transition-transform duration-200">
                  <Shield size={21} strokeWidth={1.9} />
                </span>
                <span className="leading-none">Admin</span>
              </NavLink>
            )}
          </nav>
        </aside>

        <main className="min-w-0 app-page-enter" data-testid="app-page-content">
          {children}
        </main>
      </div>
      </div>
    </div>
  );
}
